// src/redux/slices/clinicalChartModalSlice.ts

import { pdf } from "@react-pdf/renderer";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";
import { IAppointment, IClinicalChart, labs, schedulingV2, vitals } from "lobbie";
import { pdfjs } from "react-pdf";
import { MONTH_DAY_YEAR_TIME_FORMAT } from "src/constants";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import { RootState } from "src/redux/store";
import { LabVendor } from "src/schemas/settings/labs/LabVendor";
import { EChartStatus, ESearchTableType } from "src/types/global_enums";
import { LabMachineModel } from "src/types/models/labs-configuration";
import { notify } from "src/utils";
import { deleteUploadedFile, uploadFile } from "src/utils/aws";
import {
    addBloodPressureRow,
    addHeightandBMIRow,
    formatTableData,
    sortVitals,
} from "src/utils/print_table";
import { triggerSearchTableRefetch } from "./searchTableRefetchSlice";

// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#legacy-pdfjs-worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

interface PDFTableData {
    title: string;
    headers: string[];
    rows: any[]; // Use the appropriate type from your print_table.ts
}

// interface to store all chart-related data
interface ChartData {
    appointment: IAppointment;
    labTrends?: labs.ILabTrendsView;
    vitalsTrends?: vitals.IVitalsTrendsView;
    appointmentLabs: labs.ILab[];
    labVendors: any[];
    labMachines?: any[];
    labsTableData?: PDFTableData;
    vitalsTableData?: PDFTableData;
    labsPrintProps?: any[];
    logoUrl?: string;
}

interface IClinicalChartModalState {
    isOpen: boolean;
    isLoading: boolean;
    error: string | null;
    chart: schedulingV2.IClinicalChartSearchRow | null;
    pdfUrl: string | null;
    loadingProgress: number;
    scale: number;
    canUnlock: boolean;
    chartData: ChartData | null;
    executingActionForAppointmentId: number | undefined;
}

export const initialStateClinicalChartModal: IClinicalChartModalState = {
    isOpen: false,
    isLoading: false,
    error: null,
    chart: null,
    pdfUrl: null,
    loadingProgress: 0,
    scale: 0.8,
    canUnlock: false,
    chartData: null,
    executingActionForAppointmentId: undefined,
};

export const fetchChartData = async (
    appointmentId: number,
    state: RootState,
): Promise<ChartData> => {
    // Get branding from Redux state
    const location = state.location?.location;
    const account = state.account?.account;
    // Determine logo URL from existing state
    let logoUrl: string | undefined = undefined;
    if (location?.branding?.logoS3Url) {
        logoUrl = location.branding.logoS3Url;
    } else if (account?.branding?.logoS3Url) {
        logoUrl = account.branding.logoS3Url;
    }
    // Fetch appointment data
    const appointmentResponse = await axios.get(
        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}`,
        { withCredentials: true },
    );
    const appointment = appointmentResponse.data as IAppointment;
    if (!appointment) {
        throw new Error("Appointment not found");
    }
    // Use appointment's account patient ID
    const patientId = appointment.accountPatient.id;
    // Parallel fetching of all required data
    const [
        labTrendsResponse,
        vitalsTrendsResponse,
        appointmentLabsResponse,
        labVendorsResponse,
        labMachinesResponse,
    ] = await Promise.all([
        // Lab trends
        axios
            .get<labs.ILabTrendsView>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/labs/trends?accountPatientId=${patientId}&limit=5`,
                { withCredentials: true },
            )
            .catch(() => ({ data: undefined })),
        // Vitals trends
        axios
            .get<vitals.IVitalsTrendsView>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}/vitals_trends`,
                { withCredentials: true },
            )
            .catch(() => ({ data: undefined })),
        // Appointment labs
        axios
            .get<labs.ILab[]>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}/labs`,
                {
                    withCredentials: true,
                },
            )
            .catch(() => ({ data: [] })),
        // Lab vendors
        axios
            .get<LabVendor[]>(`${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/labs/vendors`, {
                withCredentials: true,
            })
            .catch(() => ({ data: [] })),
        // Lab machines
        axios
            .get<LabMachineModel[]>(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/labs/settings/machines`,
                {
                    withCredentials: true,
                },
            )
            .catch(() => ({ data: [] })),
    ]);
    const labTrends = labTrendsResponse.data;
    const vitalsTrends = vitalsTrendsResponse.data;
    const appointmentLabs = appointmentLabsResponse.data || [];
    const labVendors = labVendorsResponse.data || [];
    const labMachines = labMachinesResponse.data || [];
    // Process lab data for printing
    const labsPrintProps = appointmentLabs
        .filter((lab) => labVendors?.find((lv) => lv.vendorType === lab.labVendorType))
        .map((lab) => {
            const labVendor = labVendors?.find((lv) => lv.vendorType === lab.labVendorType);
            const labMachine = labMachines?.find((lm) => lm.id === lab.labMachineId);
            return { lab, labVendor, labMachine };
        });
    // Process table data
    let labsTableData;
    if (labTrends) {
        labsTableData = formatTableData("Labs History", labTrends);
    }
    let vitalsTableData;
    if (vitalsTrends) {
        vitalsTableData = formatTableData("Vitals", vitalsTrends);
        if (vitalsTableData) {
            addBloodPressureRow(vitalsTableData);
            addHeightandBMIRow(vitalsTableData);
            sortVitals(vitalsTableData);
        }
    }
    // Return the complete chart data
    return {
        appointment,
        labTrends,
        vitalsTrends,
        appointmentLabs,
        labVendors,
        labMachines,
        labsTableData,
        vitalsTableData,
        labsPrintProps,
        logoUrl,
    };
};

export const openChartModal = createAsyncThunk<
    ChartData,
    { chart: schedulingV2.IClinicalChartSearchRow; canUnlock: boolean },
    { state: RootState; rejectValue: string }
>("clinicalChartModal/openChartModal", async ({ chart }, { getState, rejectWithValue }) => {
    try {
        return await fetchChartData(chart.appointmentId, getState());
    } catch (error: any) {
        handleBackendHttpError(error);
        return rejectWithValue(error?.message || "Failed to fetch chart data");
    }
});

export const downloadChart = createAsyncThunk<
    void,
    { chart: schedulingV2.IClinicalChartSearchRow; ClinicalChartAppointment: any },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/downloadChart",
    async ({ chart, ClinicalChartAppointment }, { getState, rejectWithValue }) => {
        try {
            // If finalized, simply open the signed URL
            if (
                chart.chartStatus === EChartStatus.FINALIZED ||
                chart.chartStatus === EChartStatus.SIGNED_OFF
            ) {
                window.open(chart.signedURL, "_blank");
                return;
            }
            // For draft charts, get the chart data to generate PDF
            const chartData = await fetchChartData(chart.appointmentId, getState());
            // Generate PDF
            const blob = await pdf(
                ClinicalChartAppointment({
                    appointment: chartData.appointment,
                    logoUrl: chartData.logoUrl,
                    labsTableData: chartData.labsTableData,
                    vitalsTableData: chartData.vitalsTableData,
                    isFinal: false, // Not finalized
                    labsPrintProps: chartData.labsPrintProps,
                }),
            ).toBlob();
            // Create URL and open in new tab
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank");
            // Clean up URL after a delay
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 5000);
        } catch (error: any) {
            handleBackendHttpError(error);
            notify({
                level: "error",
                title: "Error",
                message: error?.message || "Failed to download chart",
            });
            return rejectWithValue(error?.message || "Failed to download chart");
        }
    },
);

export const generatePDF = createAsyncThunk<
    { url: string },
    { ClinicalChartAppointment: any },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/generatePDF",
    async ({ ClinicalChartAppointment }, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const { chartData, chart } = state.clinicalChartModal;

            if (!chartData || !chartData.appointment) {
                return rejectWithValue("Chart data not available");
            }
            // Determine if chart is finalized
            const isFinal =
                chart?.chartStatus === EChartStatus.FINALIZED ||
                chart?.chartStatus === EChartStatus.SIGNED_OFF;
            // Generate PDF using react-pdf
            const blob = await pdf(
                ClinicalChartAppointment({
                    appointment: chartData.appointment,
                    logoUrl: chartData.logoUrl,
                    labsTableData: chartData.labsTableData,
                    vitalsTableData: chartData.vitalsTableData,
                    isFinal: isFinal,
                    labsPrintProps: chartData.labsPrintProps,
                }),
            ).toBlob();
            // Create URL from blob
            const url = URL.createObjectURL(blob);
            return { url };
        } catch (error: any) {
            return rejectWithValue(error?.message || "Failed to generate PDF");
        }
    },
);

export const finalizeChart = createAsyncThunk<
    IClinicalChart,
    {
        appointmentId: number;
        ClinicalChartAppointment: any;
    },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/finalizeChart",
    async (
        { appointmentId, ClinicalChartAppointment },
        { dispatch, getState, rejectWithValue },
    ) => {
        try {
            const state = getState();
            let { chartData } = state.clinicalChartModal;
            const accountId = state.account?.account?.id;
            if (!accountId) {
                return rejectWithValue("Account ID not found");
            }
            if (!chartData || !chartData.appointment) {
                chartData = await fetchChartData(appointmentId, getState());
                if (!chartData) {
                    return rejectWithValue("Chart data not available");
                }
            }
            // Generate PDF blob
            const blob = await pdf(
                ClinicalChartAppointment({
                    appointment: chartData.appointment,
                    logoUrl: chartData.logoUrl,
                    labsTableData: chartData.labsTableData,
                    vitalsTableData: chartData.vitalsTableData,
                    isFinal: true,
                    labsPrintProps: chartData.labsPrintProps,
                }),
            ).toBlob();
            // Format current date for filename
            const now = dayjs();
            const fileName = `chart-${now.format(MONTH_DAY_YEAR_TIME_FORMAT)}.pdf`;
            const fileUpload = await uploadFile(
                { data: blob, name: fileName },
                accountId.toString(),
                {
                    onError: (error) => {
                        handleBackendHttpError(error);
                        notify({
                            level: "error",
                            title: "Error",
                            message: "Failed to upload file",
                        });
                    },
                },
            );
            if (!fileUpload) {
                return rejectWithValue("Failed to upload file");
            }
            try {
                const response = await axios.put(
                    `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/${appointmentId}/finalize_chart/${fileUpload.id}`,
                    {},
                    { withCredentials: true },
                );
                dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
                return response.data as IClinicalChart;
            } catch (error) {
                // Delete the uploaded file if finalization fails
                await deleteUploadedFile(fileUpload.s3ObjectPath);
                handleBackendHttpError(error);
                return rejectWithValue("Failed to finalize chart with uploaded file");
            }
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(error?.message || "Failed to finalize chart");
        }
    },
);

export const unlockChart = createAsyncThunk<
    void,
    { appointmentId: number },
    { state: RootState; rejectValue: string }
>("clinicalChartModal/unlockChart", async ({ appointmentId }, { dispatch, rejectWithValue }) => {
    try {
        await axios.put(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/${appointmentId}/unlock`,
            {},
            { withCredentials: true },
        );
        dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
    } catch (error: any) {
        handleBackendHttpError(error);
        return rejectWithValue(error?.message || "Failed to unlock chart");
    }
});

// Send to supervisor
export const sendToSupervisor = createAsyncThunk<
    void,
    { appointmentId: number },
    { state: RootState; rejectValue: string }
>(
    "clinicalChartModal/sendToSupervisor",
    async ({ appointmentId }, { dispatch, rejectWithValue }) => {
        try {
            await axios.put(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/clinical-chart/send-to-supervisor`,
                { appointmentId },
                { withCredentials: true },
            );
            notify({
                level: "success",
                title: "Success",
                message: "Chart sent to supervisor",
            });
            dispatch(triggerSearchTableRefetch([ESearchTableType.CLINICAL_CHARTS_DASHBOARD]));
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(error?.message || "Failed to send to supervisor");
        }
    },
);

const clinicalChartModalSlice = createSlice({
    name: "clinicalChartModal",
    initialState: initialStateClinicalChartModal,
    reducers: {
        closeChartModal: (state) => {
            state.isOpen = false;
            state.pdfUrl = null;
            // Clean up URL if exists
            if (state.pdfUrl) {
                URL.revokeObjectURL(state.pdfUrl);
                state.pdfUrl = null;
            }
            state.chartData = null;
        },
        setLoadingProgress: (state, action: PayloadAction<number>) => {
            state.loadingProgress = action.payload;
        },
        setZoomScale: (state, action: PayloadAction<number>) => {
            state.scale = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(openChartModal.pending, (state, action) => {
                state.isOpen = true;
                state.chart = action.meta.arg.chart;
                state.canUnlock = action.meta.arg.canUnlock;
                state.pdfUrl = null;
                state.loadingProgress = 0;
                state.isLoading = true;
                state.error = null;
            })
            .addCase(openChartModal.fulfilled, (state, action) => {
                state.chartData = action.payload;
                state.isLoading = false;
            })
            .addCase(openChartModal.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to open chart";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            })
            // Handle generatePDF thunk
            .addCase(generatePDF.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(generatePDF.fulfilled, (state, action) => {
                state.isLoading = false;
                // Clean up old URL if exists
                if (state.pdfUrl) {
                    URL.revokeObjectURL(state.pdfUrl);
                }
                state.pdfUrl = action.payload.url;
            })
            .addCase(generatePDF.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to generate PDF";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            })
            .addCase(finalizeChart.pending, (state, action) => {
                state.isLoading = true;
                state.executingActionForAppointmentId = action.meta.arg.appointmentId;
            })
            .addCase(finalizeChart.fulfilled, (state, action) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                if (state.chart) {
                    state.chart.chartStatus = EChartStatus.FINALIZED;
                    state.chart.returnedToProvider = false;
                    // Update file URL properties if present in response
                    if (action.payload?.signedURL) {
                        // Clean up old URL if exists
                        if (state.pdfUrl) {
                            URL.revokeObjectURL(state.pdfUrl);
                        }
                        state.pdfUrl = action.payload.signedURL;
                    }
                }
                notify({
                    level: "success",
                    title: "Success",
                    message: "Chart finalized successfully",
                });
            })
            .addCase(finalizeChart.rejected, (state, action) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                state.error = action.payload || "Failed to finalize chart";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            })
            .addCase(unlockChart.pending, (state, action) => {
                state.isLoading = true;
                state.executingActionForAppointmentId = action.meta.arg.appointmentId;
            })
            .addCase(unlockChart.fulfilled, (state) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                if (state.chart) {
                    state.chart.chartStatus = EChartStatus.DRAFT;
                }
                // Trigger a PDF refresh
                if (state.pdfUrl) {
                    URL.revokeObjectURL(state.pdfUrl);
                    state.pdfUrl = null;
                }
                notify({
                    level: "success",
                    title: "Success",
                    message: "Chart unlocked successfully",
                });
            })
            .addCase(unlockChart.rejected, (state, action) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                state.error = action.payload || "Failed to unlock chart";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            })
            .addCase(sendToSupervisor.pending, (state, action) => {
                state.isLoading = true;
                state.executingActionForAppointmentId = action.meta.arg.appointmentId;
            })
            .addCase(sendToSupervisor.fulfilled, (state) => {
                state.isLoading = false;
                state.executingActionForAppointmentId = undefined;
                if (state.chart) {
                    state.chart.returnedToProvider = false;
                }
            })
            .addCase(sendToSupervisor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to send to supervisor";
            })
            .addCase(downloadChart.pending, (state, action) => {
                state.executingActionForAppointmentId = action.meta.arg.chart.appointmentId;
            })
            .addCase(downloadChart.fulfilled, (state) => {
                state.executingActionForAppointmentId = undefined;
            })
            .addCase(downloadChart.rejected, (state) => {
                state.executingActionForAppointmentId = undefined;
            });
    },
});

export const { closeChartModal, setLoadingProgress, setZoomScale } =
    clinicalChartModalSlice.actions;

export default clinicalChartModalSlice.reducer;
